// v2.7 US-3.4: a small "?" that opens a short help bubble next to a label or
// button (docs/HoopHero-contextual-help-plan.md). Accessible toggletip:
// a real <button aria-expanded> named by visually hidden text (not
// aria-label, so label-based lookups of nearby fields never match it), and a live region that announces the text
// when it opens. Escape, a tap outside, a second tap, or opening another tip
// closes it. Renders nothing while FEATURE_HELP is off or the id is unknown.
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import useAppConfig from '../hooks/useAppConfig';
import HELP from '../help/helpText';

const GUTTER = 16;
const WIDTH = 280;
let closeOther = null; // one bubble open at a time, across the page

export default function HelpTip({ id, style }) {
  const { features } = useAppConfig();
  const entry = HELP[id];
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState({ left: 0, above: false });
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const bubbleRef = useRef(null);
  const bubbleId = useId();
  const closeSelf = useRef(() => setOpen(false));
  useEffect(() => () => { if (closeOther === closeSelf.current) closeOther = null; }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); } };
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);


  // Keep the bubble inside the viewport and off the field it explains: open
  // above the "?" when there is room (the field sits below its label), else below.
  useLayoutEffect(() => {
    if (!open || !btnRef.current || !bubbleRef.current) return;
    const b = btnRef.current.getBoundingClientRect();
    const w = Math.min(WIDTH, window.innerWidth - GUTTER * 2);
    const h = bubbleRef.current.offsetHeight;
    const left = Math.max(GUTTER, Math.min(b.left, window.innerWidth - GUTTER - w)) - b.left;
    const above = b.top - 8 - h >= GUTTER;
    setPlace({ left, above });
  }, [open]);

  if (!features?.help || !entry) return null;

  const toggle = () => {
    if (open) { setOpen(false); return; }
    if (closeOther && closeOther !== closeSelf.current) closeOther();
    closeOther = closeSelf.current;
    setOpen(true);
  };
  const topic = entry.title || 'this';

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle', ...style }}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={bubbleId}
        className="hh-help-tip"
        style={{ width: '28px', height: '28px', padding: 0, margin: '-6px 0', border: 'none', background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}
      >
        <span className="hh-sr-only">{`Help: ${topic}`}</span>
        <span aria-hidden="true" style={{ width: '16px', height: '16px', borderRadius: '50%', border: '1.5px solid #5A6070', color: '#5A6070', fontSize: '11px', fontWeight: 700, lineHeight: '13px', textAlign: 'center', fontFamily: 'inherit' }}>?</span>
      </button>
      <span id={bubbleId} role="status" style={{ position: 'absolute', left: 0, top: 0, width: 0, height: 0 }}>
        {open && (
          <span
            ref={bubbleRef}
            data-testid="help-bubble"
            style={{
              position: 'absolute', zIndex: 1000, left: `${place.left}px`, width: `min(${WIDTH}px, calc(100vw - ${GUTTER * 2}px))`,
              ...(place.above ? { bottom: '8px' } : { top: '32px' }),
              boxSizing: 'border-box', background: '#0B1A35', color: '#fff', borderRadius: '10px', padding: '10px 12px', boxShadow: '0 6px 24px rgba(11,26,53,0.25)',
              fontSize: '13px', lineHeight: 1.45, fontWeight: 400, textAlign: 'left', whiteSpace: 'normal', textTransform: 'none', letterSpacing: 'normal',
            }}
          >
            {entry.title && <strong style={{ display: 'block', marginBottom: '2px', fontWeight: 700 }}>{entry.title}</strong>}
            {entry.body}
          </span>
        )}
      </span>
    </span>
  );
}
